import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Clock3, PhoneCall } from "lucide-react";

// HighLevel form embed — official iframe from LeadConnector.
// The script `form_embed.js` handles auto-resize of the iframe. We provide a
// responsive min-height fallback so the form is never cut before the script
// takes over, and never displays a double scrollbar (the iframe grows, the
// outer container does not scroll internally).

const HL_SRC = "https://api.leadconnectorhq.com/widget/form/So4yQ0SZIxf6G4bxVWNM";
const HL_FORM_ID = "So4yQ0SZIxf6G4bxVWNM";
const HL_SCRIPT_SRC = "https://link.msgsndr.com/js/form_embed.js";

function useHighLevelEmbedScript() {
    useEffect(() => {
        // Load the embed script only once, globally.
        if (document.querySelector(`script[src="${HL_SCRIPT_SRC}"]`)) return;
        const s = document.createElement("script");
        s.src = HL_SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        document.body.appendChild(s);
    }, []);
}

export default function SoumissionHighLevel() {
    useHighLevelEmbedScript();

    return (
        <div className="bg-[#F8F5EE] min-h-screen">
            {/* Hero — night-blue premium */}
            <section
                className="relative overflow-hidden pt-14 pb-14 lg:pt-24 lg:pb-20"
                style={{ background: "linear-gradient(135deg,#020713 0%,#041126 55%,#07204A 130%)" }}
                data-theme="dark"
                data-testid="soumission-hero"
            >
                <div aria-hidden="true" className="absolute -right-24 -top-24 w-[560px] h-[560px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(232,212,139,0.16), transparent 70%)" }}/>
                <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(232,212,139,0.55), transparent)" }}/>

                <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
                    <div className="eyebrow" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Évaluation gratuite</div>
                    <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-white mt-3 leading-[1.08]">
                        Prenons soin de vos espaces<br className="hidden sm:block"/>
                        <span style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37,#B8912B)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>ensemble.</span>
                    </h1>
                    <div className="mx-auto mt-6 h-px w-24" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <p className="mt-6 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "#E8EEF7", opacity: 0.95 }}>
                        Partagez-nous quelques détails sur votre espace et vos besoins. Nous vous recontactons rapidement pour planifier une visite d'évaluation, sans engagement.
                    </p>

                    <div className="mt-8 flex flex-wrap justify-center gap-2.5">
                        {[
                            { icon: ShieldCheck, t: "Sans engagement" },
                            { icon: Clock3,      t: "Réponse sous 24 h ouvrables" },
                            { icon: PhoneCall,   t: "Suivi personnalisé" },
                        ].map(({ icon: Icon, t }) => (
                            <div key={t} className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] sm:text-xs uppercase tracking-widest" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(232,212,139,0.35)", color: "#E8D48B" }}>
                                <Icon className="w-3.5 h-3.5" style={{ color: "#E8D48B" }}/>
                                <span>{t}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form section — off-white, elegant frame around the HighLevel iframe */}
            <section id="formulaire" className="relative max-w-3xl mx-auto px-5 lg:px-8 py-12 lg:py-16" data-testid="soumission-form-section">
                <div aria-hidden="true" className="absolute -top-6 left-1/2 -translate-x-1/2 w-24 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.7), transparent)" }}/>
                <div
                    className="relative rounded-2xl overflow-hidden bg-white shadow-[0_30px_70px_-24px_rgba(4,17,38,0.25)]"
                    style={{ border: "1px solid rgba(212,175,55,0.35)" }}
                    data-testid="soumission-form-card"
                >
                    {/* Gold accent line at top of card */}
                    <div aria-hidden="true" className="h-[3px]" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37,#B8912B)" }}/>

                    <div className="px-3 sm:px-5 lg:px-6 py-4 sm:py-6">
                        {/* Responsive fallback height: tall enough that no fields are cut before form_embed.js resizes. Once the script loads, it overrides the height dynamically. */}
                        <div className="hl-form-shell w-full">
                            <iframe
                                src={HL_SRC}
                                id={`inline-${HL_FORM_ID}`}
                                className="hl-form-iframe"
                                style={{
                                    width: "100%",
                                    border: "none",
                                    borderRadius: "8px",
                                    display: "block",
                                }}
                                data-layout='{"id":"INLINE"}'
                                data-trigger-type="alwaysShow"
                                data-trigger-value=""
                                data-activation-type="alwaysActivated"
                                data-activation-value=""
                                data-deactivation-type="neverDeactivate"
                                data-deactivation-value=""
                                data-form-name="Evaluation gratuite - site web ménage KGD"
                                data-height="undefined"
                                data-layout-iframe-id={`inline-${HL_FORM_ID}`}
                                data-form-id={HL_FORM_ID}
                                data-cookie-consent="true"
                                data-cookie-consent-provider="auto"
                                title="Évaluation gratuite - site web Ménage KGD"
                                data-testid="highlevel-form-iframe"
                                loading="eager"
                                referrerPolicy="no-referrer-when-downgrade"
                                allow="clipboard-write; camera; microphone"
                            />
                        </div>
                    </div>
                </div>

                <p className="text-center text-xs text-slate-500 mt-6 italic px-4">
                    Vos informations restent confidentielles. Elles nous servent uniquement à préparer votre évaluation. Consultez notre <Link to="/confidentialite" className="text-[#8C6D1F] hover:underline">politique de confidentialité</Link>.
                </p>
            </section>

            {/* Reassurance band */}
            <section className="relative py-16 lg:py-20 border-t border-kgd-gold/20" style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F4EFE1 100%)" }} data-testid="soumission-reassurance">
                <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
                    <div className="eyebrow">Après votre demande</div>
                    <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#041126] mt-3">Un accompagnement à la hauteur de vos attentes</h2>
                    <div className="mx-auto mt-4 h-px w-16" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
                        {[
                            { n: "01", t: "Contact rapide", d: "Nous vous joignons sous 24 h ouvrables pour discuter de vos besoins." },
                            { n: "02", t: "Visite d'évaluation", d: "Une rencontre courtoise sur place, sans obligation, pour bien cerner votre espace." },
                            { n: "03", t: "Proposition personnalisée", d: "Une soumission claire et adaptée à vos priorités et à votre rythme." },
                        ].map(({ n, t, d }) => (
                            <div key={n} className="luxe-card p-5 text-left">
                                <div className="flex items-baseline gap-2">
                                    <span className="font-serif text-lg text-[#B8912B]">{n}</span>
                                    <span className="h-px flex-1 opacity-40" style={{ background: "linear-gradient(90deg,#D4AF37,transparent)" }}/>
                                </div>
                                <div className="font-serif text-lg text-[#041126] mt-2">{t}</div>
                                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 text-[11px] uppercase tracking-[0.28em] text-[#7A6320] flex items-center justify-center gap-3">
                        <span>Rigueur</span>
                        <span className="w-1 h-1 rounded-full" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                        <span>Constance</span>
                        <span className="w-1 h-1 rounded-full" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                        <span>Tranquillité d'esprit</span>
                    </div>
                </div>
            </section>
        </div>
    );
}
